/**
 * Theme resolution utilities (ADR-013).
 *
 * Web surfaces follow the device theme by default. A resident may store an
 * explicit override; when set, the override wins over the OS preference.
 *
 * `securelabs.theme` holds a non-sensitive UI preference only — it is NOT auth
 * state, so localStorage is appropriate here (ADR-001 governs auth state, which
 * stays in HttpOnly cookies).
 */

export type ThemePreference = 'system' | 'light' | 'dark';
export type ResolvedTheme = 'light' | 'dark';

export const THEME_STORAGE_KEY = 'securelabs.theme';

/** Browser-chrome colour per resolved theme — keep in sync with --bg-page. */
export const THEME_COLORS: Record<ResolvedTheme, string> = {
  dark: '#05140a', // Forest Noir (sl.noir.100)
  light: '#f8f3e7', // Paper
};

const PREFERENCES: readonly ThemePreference[] = ['system', 'light', 'dark'];

function isThemePreference(value: unknown): value is ThemePreference {
  return typeof value === 'string' && (PREFERENCES as readonly string[]).includes(value);
}

/** Read the stored override; defaults to 'system' (follow the device). */
export function getStoredPreference(): ThemePreference {
  try {
    const raw = localStorage.getItem(THEME_STORAGE_KEY);
    return isThemePreference(raw) ? raw : 'system';
  } catch {
    // localStorage can throw in private-mode / sandboxed contexts.
    return 'system';
  }
}

export function storePreference(pref: ThemePreference): void {
  try {
    if (pref === 'system') {
      localStorage.removeItem(THEME_STORAGE_KEY);
    } else {
      localStorage.setItem(THEME_STORAGE_KEY, pref);
    }
  } catch {
    // Best-effort; a failed write simply means the choice is not persisted.
  }
}

/** True when the device currently prefers dark. */
export function devicePrefersDark(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-color-scheme: dark)').matches
  );
}

/** Resolve a preference to a concrete theme using the device when 'system'. */
export function resolveTheme(pref: ThemePreference): ResolvedTheme {
  if (pref === 'system') return devicePrefersDark() ? 'dark' : 'light';
  return pref;
}

/**
 * Apply a resolved theme to the document: toggle the html class (drives both
 * the semantic tokens and the Tailwind `dark:` variant) and update the
 * theme-color meta so mobile browser chrome matches.
 */
export function applyTheme(resolved: ResolvedTheme): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.classList.remove('light', 'dark');
  root.classList.add(resolved);

  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', THEME_COLORS[resolved]);
}
